import React, { useState } from 'react';
import { TotpMultiFactorGenerator, multiFactor } from 'firebase/auth';
import { QRCodeSVG } from 'qrcode.react';
import './MFA.css';

export default function MFAEinrichten({ nutzer, onFertig, onAbbrechen }) {
  const [schritt, setSchritt]       = useState('start'); // start | qr | code | fertig
  const [totpSecret, setTotpSecret] = useState(null);
  const [qrUrl, setQrUrl]           = useState('');
  const [code, setCode]             = useState('');
  const [fehler, setFehler]         = useState(null);
  const [laden, setLaden]           = useState(false);
  const [secretSichtbar, setSecretSichtbar] = useState(false);
  const [kopiert, setKopiert]       = useState(false);

  const startEnrollment = async () => {
    setFehler(null);
    setLaden(true);
    try {
      const mfaUser = multiFactor(nutzer);
      const session = await mfaUser.getSession();
      const secret  = await TotpMultiFactorGenerator.generateSecret(session);
      const url     = secret.generateQrCodeUrl(nutzer.email, 'CVFlow');
      setTotpSecret(secret);
      setQrUrl(url);
      setSchritt('qr');
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setFehler('Bitte melde dich erneut an, um 2FA einzurichten. Deine Sitzung ist zu alt.');
      } else {
        setFehler('Fehler beim Einrichten. Bitte versuche es erneut.');
      }
    } finally {
      setLaden(false);
    }
  };

  const bestaetigen = async () => {
    if (code.replace(/\D/g, '').length < 6) {
      setFehler('Bitte gib den 6-stelligen Code aus deiner App ein.');
      return;
    }
    setFehler(null);
    setLaden(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        code.replace(/\D/g, '')
      );
      await multiFactor(nutzer).enroll(assertion, 'Authenticator App');
      setSchritt('fertig');
    } catch (err) {
      setFehler('Ungültiger Code. Bitte prüfe deine App — der Code läuft alle 30 Sekunden ab.');
    } finally {
      setLaden(false);
    }
  };

  const authenticatorOeffnen = () => {
    if (!qrUrl) return;
    window.location.href = qrUrl;
  };

  const schluesselKopieren = async () => {
    if (!totpSecret?.secretKey) return;
    try {
      await navigator.clipboard.writeText(totpSecret.secretKey);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1800);
    } catch {
      setFehler('Kopieren nicht möglich. Markiere den Schlüssel und kopiere ihn manuell.');
      setSecretSichtbar(true);
    }
  };

  if (schritt === 'start') {
    return (
      <div className="mfa-modal">
        <div className="mfa-modal-header">
          <span className="mfa-modal-icon">🔐</span>
          <h2>Zwei-Faktor-Authentifizierung einrichten</h2>
          <p>
            Schütze dein Konto mit einer Authenticator-App wie{' '}
            <strong>Google Authenticator</strong>, <strong>Authy</strong> oder{' '}
            <strong>Microsoft Authenticator</strong>.
          </p>
        </div>
        <div className="mfa-steps-preview">
          <div className="mfa-step-item"><span className="mfa-step-num">1</span>App installieren &amp; öffnen</div>
          <div className="mfa-step-item"><span className="mfa-step-num">2</span>QR-Code scannen</div>
          <div className="mfa-step-item"><span className="mfa-step-num">3</span>Code bestätigen</div>
        </div>
        {fehler && <div className="mfa-error">{fehler}</div>}
        <div className="mfa-btn-row">
          <button className="mfa-btn-primary" onClick={startEnrollment} disabled={laden}>
            {laden ? <span className="auth-spinner" /> : 'Einrichten starten'}
          </button>
          <button className="mfa-btn-ghost" onClick={onAbbrechen}>Abbrechen</button>
        </div>
      </div>
    );
  }

  if (schritt === 'qr') {
    return (
      <div className="mfa-modal">
        <div className="mfa-modal-header">
          <span className="mfa-modal-icon">📱</span>
          <h2>QR-Code scannen</h2>
          <p>Scanne den QR-Code am Desktop oder öffne die Authenticator-App direkt auf diesem Gerät.</p>
        </div>
        <button className="mfa-open-authenticator" onClick={authenticatorOeffnen}>
          Authenticator-App öffnen
        </button>
        <div className="mfa-qr-container">
          <QRCodeSVG value={qrUrl} size={200} level="M" includeMargin />
        </div>
        <div className="mfa-secret-reveal">
          <button
            className="mfa-secret-toggle"
            onClick={() => setSecretSichtbar(v => !v)}
          >
            {secretSichtbar ? 'Schlüssel verbergen' : 'Schlüssel manuell eingeben'}
          </button>
          {secretSichtbar && (
            <div className="mfa-secret-box">
              <span className="mfa-secret-label">Geheimer Schlüssel</span>
              <code className="mfa-secret-key">{totpSecret?.secretKey}</code>
              <button className="mfa-copy-secret" onClick={schluesselKopieren}>
                {kopiert ? 'Kopiert' : 'Schlüssel kopieren'}
              </button>
              <span className="mfa-secret-hint">Gib diesen Schlüssel manuell in deine App ein, falls der QR-Code nicht funktioniert.</span>
            </div>
          )}
        </div>
        <div className="mfa-btn-row">
          <button className="mfa-btn-primary" onClick={() => setSchritt('code')}>
            Weiter → Code eingeben
          </button>
          <button className="mfa-btn-ghost" onClick={onAbbrechen}>Abbrechen</button>
        </div>
      </div>
    );
  }

  if (schritt === 'code') {
    return (
      <div className="mfa-modal">
        <div className="mfa-modal-header">
          <span className="mfa-modal-icon">✅</span>
          <h2>Code bestätigen</h2>
          <p>Gib den aktuellen 6-stelligen Code aus deiner App ein, um die Einrichtung abzuschließen.</p>
        </div>
        <input
          className="mfa-code-input"
          type="text"
          inputMode="numeric"
          placeholder="000 000"
          maxLength={7}
          value={code}
          onChange={e => { setCode(e.target.value); setFehler(null); }}
          onKeyDown={e => e.key === 'Enter' && bestaetigen()}
          autoFocus
        />
        {fehler && <div className="mfa-error">{fehler}</div>}
        <div className="mfa-btn-row">
          <button className="mfa-btn-primary" onClick={bestaetigen} disabled={laden}>
            {laden ? <span className="auth-spinner" /> : '2FA aktivieren'}
          </button>
          <button className="mfa-btn-ghost" onClick={() => setSchritt('qr')}>← Zurück</button>
        </div>
      </div>
    );
  }

  if (schritt === 'fertig') {
    return (
      <div className="mfa-modal">
        <div className="mfa-modal-header">
          <span className="mfa-modal-icon">🎉</span>
          <h2>2FA erfolgreich aktiviert!</h2>
          <p>
            Dein Konto ist jetzt mit Zwei-Faktor-Authentifizierung geschützt.
            Bei jeder Anmeldung wirst du nach einem Code gefragt.
          </p>
        </div>
        <div className="mfa-success-banner">
          <span className="mfa-success-check">✓</span>
          Authenticator App verknüpft
        </div>
        <div className="mfa-btn-row">
          <button className="mfa-btn-primary" onClick={onFertig}>Fertig</button>
        </div>
      </div>
    );
  }

  return null;
}
